import { ConfirmDeleteDialog } from '@/components/app/confirm-delete-dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import {
  type Asset,
  assetAnalysisStatusLabels,
  useDeleteContentVersion,
  useGetAssetAnalysis,
  useReanalyzeAsset,
} from '@/features/assets';
import {
  claimAnalysisRetry,
  releaseAnalysisRetry,
  useAnalysisTrackerStore,
} from '@/features/assets/background-analysis/analysis-tracker.store';
// Deep import: the assistant barrel reaches the router, and a gallery dialog
// that pulls the router in cannot be rendered on its own in a test.
import { createAssistantHandoff } from '@/features/assistant/lib/pending-handoff';
import type { Graphic, GraphicOutput } from '@/features/graphics';
import { type Video, useGetVideo } from '@/features/videos';
import { queryKeys } from '@/lib/query-keys';
import { cn } from '@/lib/utils';
import { apiClient } from '@borradh-workspace/api-client';
import {
  type ContentVersionsResponse,
  contentVersionsResponseSchema,
  graphicSchema,
} from '@borradh-workspace/contracts';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  AlertTriangle,
  Download,
  Image as ImageIcon,
  Loader2,
  RefreshCw,
  ShieldAlert,
  Sparkles,
  Trash2,
  Video as VideoIcon,
} from 'lucide-react';
import { useEffect, useState } from 'react';

import { AssetServiceCombobox } from './asset-service-combobox';
import { ContentVersionSelect } from './content-version-select';
import {
  attachableUrls,
  attachmentBaseName,
  fetchGalleryAttachments,
} from './edit-in-claire';
import { failedGraphicCopy } from './failed-graphic';

/** Phone-width action columns (≥420px), one per action in the footer row. */
const ACTION_COLUMNS: Record<number, string> = {
  1: 'min-[420px]:grid-cols-1',
  2: 'min-[420px]:grid-cols-2',
  3: 'min-[420px]:grid-cols-3',
};

// ─── Unified media item: the three things the gallery can show ───

export type GalleryMediaItem =
  | { kind: 'asset'; data: Asset }
  | { kind: 'video'; data: Video }
  | { kind: 'graphic'; data: Graphic & { itemId?: string | null } };

interface MediaPreviewDialogProps {
  item: GalleryMediaItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDelete?: (item: GalleryMediaItem) => void;
  isDeleting?: boolean;
}

function isVideoItem(item: GalleryMediaItem): boolean {
  return (
    item.kind === 'video' ||
    (item.kind === 'asset' && item.data.type === 'video')
  );
}

/**
 * A graphic's `outputs` array holds one rendered image per slide (carousel
 * support — see `GraphicOutput.slideOrder`). Returns the slides with a usable
 * URL, sorted by slide order so the carousel matches the editor.
 */
function graphicSlides(item: GalleryMediaItem): GraphicOutput[] {
  if (item.kind !== 'graphic') return [];
  return [...(item.data.outputs ?? [])]
    .filter((o) => o.url)
    .sort((a, b) => (a.slideOrder ?? 0) - (b.slideOrder ?? 0));
}

function UploadedAssetAnalysis({ asset }: { asset: Asset }) {
  const { analysis, isLoading, refetch } = useGetAssetAnalysis(asset.id);
  const { reanalyzeAssetAsync, isReanalyzing } = useReanalyzeAsset();
  const trackedStatus = useAnalysisTrackerStore(
    (state) => state.tracked[asset.id]?.status
  );
  const status =
    trackedStatus && trackedStatus !== 'abandoned'
      ? trackedStatus === 'pending'
        ? 'queued'
        : trackedStatus
      : analysis?.status;

  const retryAnalysis = async () => {
    if (!claimAnalysisRetry(asset.id)) return;
    try {
      await reanalyzeAssetAsync(asset.id);
      const tracker = useAnalysisTrackerStore.getState();
      tracker.untrack([asset.id]);
      tracker.track([
        { assetId: asset.id, label: asset.sourceFileName ?? asset.name },
      ]);
    } catch {
      return;
    } finally {
      releaseAnalysisRetry(asset.id);
    }
  };

  return (
    <div className="flex min-w-0 items-center justify-between gap-2 border-t pt-3">
      <div className="flex min-w-0 items-center gap-2 text-sm">
        <span>Analysis Status</span>
        <span className="text-muted-foreground" role="status">
          {isLoading && !status
            ? 'Loading…'
            : status
              ? assetAnalysisStatusLabels[status]
              : 'Not analyzed'}
        </span>
      </div>
      {status !== 'processing' && (
        <Button
          variant="ghost"
          size="sm"
          className="shrink-0"
          onClick={() => {
            if (status === 'failed') void retryAnalysis();
            else void refetch();
          }}
          disabled={isLoading || isReanalyzing}
        >
          {isReanalyzing ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <RefreshCw className="size-4" />
          )}
          {status === 'failed' ? 'Retry' : 'Refresh'}
        </Button>
      )}
    </div>
  );
}

/**
 * One preview dialog shared across images, uploaded videos and AI-generated
 * graphics/videos. Shows the media large with delete/download/edit actions.
 */
export function MediaPreviewDialog({
  item: sourceItem,
  open,
  onOpenChange,
  onDelete,
  isDeleting,
}: MediaPreviewDialogProps) {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const navigate = useNavigate();
  const [isOpeningChat, setIsOpeningChat] = useState(false);

  const sourceId = sourceItem?.data.id;
  const sourceKind = sourceItem?.kind;
  // An uploaded image gains versions through "Edit content"; its edits are
  // graphics, so it reads and shows them the way a graphic's versions are.
  const isUploadedImage =
    sourceItem?.kind === 'asset' && sourceItem.data.type === 'image';
  const hasVersions = sourceKind !== 'asset' || isUploadedImage;
  const versionsGraphics = sourceKind === 'graphic' || isUploadedImage;
  const [selection, setSelection] = useState<{
    sourceId: string;
    assetId: string;
  }>();
  // The version the history is read through. Normally the item that was
  // opened; once that version is deleted, the version that took over.
  const [anchor, setAnchor] = useState<{
    sourceId: string;
    assetId: string;
  }>();
  useEffect(() => {
    if (selection && (!open || selection.sourceId !== sourceId))
      setSelection(undefined);
    if (anchor && (!open || anchor.sourceId !== sourceId)) setAnchor(undefined);
  }, [sourceId, open, selection, anchor]);
  const anchorId =
    anchor && anchor.sourceId === sourceId ? anchor.assetId : sourceId;
  const versionsQuery = useQuery({
    queryKey: queryKeys.content.versions(sourceKind ?? 'asset', anchorId ?? ''),
    queryFn: () =>
      apiClient.get<ContentVersionsResponse>(
        `content-batches/media/${sourceKind}/${anchorId}/versions`,
        { schema: contentVersionsResponseSchema }
      ),
    enabled: open && Boolean(sourceId) && hasVersions,
    staleTime: 0,
  });
  const selectedId =
    selection && selection.sourceId === sourceId
      ? selection.assetId
      : (versionsQuery.data?.currentAssetId ?? sourceId ?? '');
  const selectedGraphic = useQuery({
    queryKey: queryKeys.content.graphic(selectedId),
    queryFn: () =>
      apiClient.get<Graphic>(`graphics/${selectedId}`, {
        schema: graphicSchema,
      }),
    enabled: open && versionsGraphics && selectedId !== sourceId,
    staleTime: 0,
  });
  const selectedGraphicData =
    selectedGraphic.data?.id === selectedId ? selectedGraphic.data : null;
  const item: GalleryMediaItem | null =
    (sourceItem?.kind === 'graphic' || isUploadedImage) && selectedGraphicData
      ? { kind: 'graphic', data: selectedGraphicData }
      : sourceItem?.kind === 'video' && selectedId !== sourceId
        ? {
            kind: 'video',
            data: {
              ...sourceItem.data,
              id: selectedId,
              blobUrl: null,
              thumbnailUrl: null,
            },
          }
        : sourceItem;
  const loadingGraphicVersion =
    versionsGraphics && selectedId !== sourceId && !selectedGraphicData;
  const versions =
    versionsQuery.data?.versions ??
    (sourceId ? [{ assetId: sourceId, version: 1 }] : []);
  // Every version has its own Delete. An edited upload version (v2+) and any
  // version of a generated graphic with a history are deleted on their own;
  // an upload's v1 is the upload and goes through the gallery's own delete,
  // history included. A generated graphic's last version takes the graphic.
  const historyItemId = versionsQuery.data?.itemId ?? null;
  const deletingVersion =
    Boolean(sourceId && historyItemId) &&
    (isUploadedImage ? selectedId !== sourceId : sourceKind === 'graphic');
  const deletingLastVersion =
    deletingVersion && !isUploadedImage && versions.length < 2;
  const { deleteContentVersion, isDeletingVersion } = useDeleteContentVersion({
    onSuccess: ({ fallbackAssetId, deletedWholeGraphic }) => {
      setConfirmDelete(false);
      if (deletedWholeGraphic || !sourceId) {
        onOpenChange(false);
        return;
      }
      if (!fallbackAssetId) return;
      // The opened version itself was deleted: read the history through the
      // version that took over.
      if (selectedId === anchorId)
        setAnchor({ sourceId, assetId: fallbackAssetId });
      setSelection({ sourceId, assetId: fallbackAssetId });
    },
  });
  const canDeleteGraphic =
    sourceKind !== 'graphic' ||
    (versionsQuery.isSuccess && !versionsQuery.isFetching);

  // AI-generated videos null out blobUrl in the list endpoint — fetch the
  // presigned URL individually when previewing one.
  const {
    video: fullVideo,
    isLoading,
    isError,
    refetch,
  } = useGetVideo(item?.kind === 'video' ? item.data.id : '', {
    enabled: open && item?.kind === 'video' && item.data.status === 'ready',
  });

  // Track the visible slide for multi-slide graphics so the counter and the
  // download button follow the carousel.
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [currentSlide, setCurrentSlide] = useState(0);

  // Reset to the first slide whenever a different item is previewed.
  const itemId = item?.data.id;
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset keyed on item id
  useEffect(() => {
    setCurrentSlide(0);
    carouselApi?.scrollTo(0, true);
  }, [itemId, selectedId]);

  useEffect(() => {
    if (!carouselApi) return;
    setCurrentSlide(carouselApi.selectedScrollSnap());
    const onSelect = () => setCurrentSlide(carouselApi.selectedScrollSnap());
    carouselApi.on('select', onSelect);
    return () => {
      carouselApi.off('select', onSelect);
    };
  }, [carouselApi]);

  if (!item) return null;

  const isVideo = isVideoItem(item);
  // A failed graphic shows why it failed, not whatever slides it left behind.
  const graphicFailure =
    item.kind === 'graphic' ? failedGraphicCopy(item.data) : null;
  const slides =
    loadingGraphicVersion || graphicFailure ? [] : graphicSlides(item);
  const isCarousel = slides.length > 1;

  let title = 'Untitled';
  let url: string | null = null;
  if (item.kind === 'asset') {
    title = item.data.name;
    url = item.data.blobUrl;
  } else if (item.kind === 'video') {
    title = item.data.title || 'Untitled';
    // A thumbnail is not a playable/downloadable video. Details may still be
    // loading, and cached data must belong to the selected item.
    url =
      (fullVideo?.id === item.data.id ? fullVideo.blobUrl : null) ??
      item.data.blobUrl ??
      null;
  } else {
    title = item.data.title || 'Untitled';
    // Download follows the visible slide; falls back to the first.
    url = slides[currentSlide]?.url ?? slides[0]?.url ?? null;
  }

  /**
   * Open a chat with this piece of content already sent.
   *
   * Two things travel, and they do different jobs. The ATTACHMENT is what the
   * owner sees — the thing they clicked, sitting in the transcript as if they
   * had attached it — and what the model looks at. The ENTITY PAIR is what the
   * editing tools take: the chat API resolves it to the content item that owns
   * the asset, adopting one for content that predates item tracking.
   *
   * The attachment is best-effort (see `fetchAttachments`). If the fetch is
   * refused we still open the chat and still send — a missing picture is worth
   * less than the ids, and Claire can read the content through them.
   */
  const openInClaire = async () => {
    if (isOpeningChat || loadingGraphicVersion) return;
    if (isUploadedImage) return openUploadInClaire();
    if (item.kind === 'asset') return;
    setIsOpeningChat(true);
    try {
      const uploads = await fetchGalleryAttachments(
        attachableUrls({
          kind: item.kind,
          slideUrls: isCarousel
            ? [slides[currentSlide]?.url]
            : slides.map((slide) => slide.url),
          thumbnailUrl:
            item.kind === 'video'
              ? (fullVideo?.thumbnailUrl ?? item.data.thumbnailUrl)
              : null,
        }),
        attachmentBaseName(title, item.kind),
        item.kind,
        item.data.id,
        isCarousel ? currentSlide : undefined
      );
      const handoff = createAssistantHandoff({
        draft:
          item.kind === 'graphic'
            ? isCarousel
              ? `Edit slide ${currentSlide + 1}`
              : 'Edit this graphic'
            : 'Edit this video',
        uploads,
      });
      await navigate({
        to: '/assistant',
        search: {
          new: '1',
          handoff,
          entityType: item.kind,
          entityId: item.data.id,
        },
      });
    } finally {
      setIsOpeningChat(false);
    }
  };

  /**
   * "Edit content" on an uploaded image: the same hand-off, naming the version
   * on screen. v1 is the upload itself (`asset`); a later version is the
   * graphic that holds that edit, which already resolves to the upload's
   * item. Claire edits it with the image model and saves the next version.
   */
  async function openUploadInClaire() {
    if (!sourceId) return;
    const onOriginal = selectedId === sourceId;
    const kind = onOriginal ? ('asset' as const) : ('graphic' as const);
    const id = onOriginal ? sourceId : selectedId;
    setIsOpeningChat(true);
    try {
      const uploads = await fetchGalleryAttachments(
        url ? [url] : [],
        attachmentBaseName(title, 'image'),
        kind,
        id
      );
      const handoff = createAssistantHandoff({
        draft: 'Edit this image',
        uploads,
      });
      await navigate({
        to: '/assistant',
        search: { new: '1', handoff, entityType: kind, entityId: id },
      });
    } finally {
      setIsOpeningChat(false);
    }
  }

  // Which footer actions show, so phones can lay them out as equal columns.
  // An uploaded video only has a service to assign; everything else can be
  // edited (uploads with "Edit content", generated content with Claire).
  const showEditAction = sourceItem?.kind !== 'asset' || isUploadedImage;
  const showDeleteAction =
    Boolean(onDelete) &&
    canDeleteGraphic &&
    (deletingVersion ||
      selectedId === (versionsQuery.data?.currentAssetId ?? sourceId));
  const footerActionCount =
    1 + (showEditAction ? 1 : 0) + (showDeleteAction ? 1 : 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[calc(100dvh-2rem)] min-w-0 grid-cols-[minmax(0,1fr)] overflow-y-auto sm:max-w-2xl"
        aria-describedby={undefined}
      >
        <div className="flex min-w-0 items-center gap-3 pr-8">
          <DialogTitle
            className="min-w-0 flex-1 truncate text-base"
            title={title}
          >
            {title}
          </DialogTitle>
          {hasVersions && (
            <ContentVersionSelect
              value={selectedId}
              options={versions.map((version) => ({
                assetId: version.assetId,
                label: `v${version.version}`,
              }))}
              onValueChange={(assetId) => {
                if (sourceId) setSelection({ sourceId, assetId });
              }}
              disabled={versions.length < 2}
            />
          )}
        </div>
        {versionsQuery.isError && hasVersions && (
          <Alert>
            <AlertDescription>
              Versions could not be loaded.{' '}
              <Button
                variant="link"
                onClick={() => void versionsQuery.refetch()}
              >
                Retry
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {isCarousel ? (
          <div className="relative min-w-0">
            <Carousel setApi={setCarouselApi} className="w-full">
              <CarouselContent>
                {slides.map((slide, index) => (
                  <CarouselItem key={slide.slideId ?? `${slide.url}-${index}`}>
                    <div className="flex max-h-[62vh] items-center justify-center overflow-hidden rounded-lg bg-muted">
                      <img
                        src={slide.url}
                        alt={`${title} — slide ${index + 1}`}
                        className="max-h-[62vh] w-auto max-w-full object-contain"
                      />
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious className="left-2" />
              <CarouselNext className="right-2" />
            </Carousel>
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-2 py-0.5 text-xs font-medium text-white">
              {currentSlide + 1} / {slides.length}
            </div>
          </div>
        ) : (
          <div className="flex min-w-0 max-h-[62vh] items-center justify-center overflow-hidden rounded-lg bg-muted">
            {loadingGraphicVersion ? (
              selectedGraphic.isError ? (
                <Alert>
                  <AlertDescription>
                    We could not load this version.{' '}
                    <Button
                      variant="outline"
                      onClick={() => void selectedGraphic.refetch()}
                    >
                      Retry
                    </Button>
                  </AlertDescription>
                </Alert>
              ) : (
                <div role="status" className="w-full">
                  <Skeleton className="h-[50vh] w-full" />
                  <span className="sr-only">Loading version...</span>
                </div>
              )
            ) : url ? (
              isVideo ? (
                // biome-ignore lint/a11y/useMediaCaption: user-generated content
                <video
                  src={url}
                  controls
                  className="max-h-[62vh] w-auto max-w-full object-contain"
                />
              ) : (
                <img
                  src={url}
                  alt={title}
                  className="max-h-[62vh] w-auto max-w-full object-contain"
                />
              )
            ) : item.kind === 'video' ? (
              isLoading ? (
                <div
                  role="status"
                  className="flex w-full flex-col items-center gap-3 p-4"
                >
                  <Skeleton className="aspect-video w-full" />
                  <span>Loading video...</span>
                </div>
              ) : (
                <Alert>
                  <AlertDescription>
                    {isError
                      ? 'We could not load this video.'
                      : 'The video file is not available yet.'}
                    <Button variant="outline" onClick={() => void refetch()}>
                      Retry
                    </Button>
                  </AlertDescription>
                </Alert>
              )
            ) : graphicFailure ? (
              <div
                data-testid="media-preview-failed"
                className="flex aspect-video w-full flex-col items-center justify-center gap-2 bg-destructive/5 px-6 text-center"
              >
                {graphicFailure.isSafetyBlock ? (
                  <ShieldAlert className="size-8 text-destructive" />
                ) : (
                  <AlertTriangle className="size-8 text-destructive" />
                )}
                <p className="text-sm font-medium text-destructive">
                  {graphicFailure.title}
                </p>
                <p className="max-w-sm text-xs text-muted-foreground">
                  {graphicFailure.message}
                </p>
                {graphicFailure.code ? (
                  <p className="text-xs font-medium">
                    Error code:{' '}
                    <span className="select-all font-mono">
                      {graphicFailure.code}
                    </span>
                  </p>
                ) : null}
              </div>
            ) : (
              <div className="flex aspect-video w-full items-center justify-center text-muted-foreground">
                {isVideo ? (
                  <VideoIcon className="size-12" />
                ) : (
                  <ImageIcon className="size-12" />
                )}
              </div>
            )}
          </div>
        )}

        {item.kind === 'asset' && item.data.type === 'video' && (
          <UploadedAssetAnalysis asset={item.data} />
        )}

        {/* Phones: the Service picker on its own row, then the actions as one
            row of equal buttons (stacked full width below ~420px). From
            `sm` up the groups are real flex rows again: edit on the left,
            delete/download on the right, exactly as on desktop. */}
        <div
          className={cn(
            'grid min-w-0 grid-cols-1 gap-2 sm:flex sm:flex-row sm:items-end sm:justify-between sm:gap-3',
            ACTION_COLUMNS[footerActionCount] ?? ACTION_COLUMNS[3]
          )}
        >
          {/* Bottom-left: assign a service to an upload, or hand content to
              Claire to change. */}
          <div className="contents sm:flex sm:min-w-0 sm:flex-wrap sm:items-end sm:gap-2">
            {sourceItem?.kind === 'asset' && (
              <div className="col-span-full min-w-0 sm:col-auto">
                <AssetServiceCombobox
                  key={sourceItem.data.id}
                  assetId={sourceItem.data.id}
                  currentServices={sourceItem.data.services ?? []}
                />
              </div>
            )}
            {showEditAction && (
              <Button
                variant="outline"
                className="w-full sm:w-auto"
                disabled={
                  isOpeningChat ||
                  loadingGraphicVersion ||
                  (isUploadedImage ? !url : item.kind === 'video' && !url)
                }
                onClick={() => {
                  void openInClaire();
                }}
              >
                <Sparkles className="size-4" />
                {isOpeningChat
                  ? 'Opening...'
                  : isUploadedImage
                    ? 'Edit content'
                    : item.kind === 'graphic'
                      ? isCarousel
                        ? 'Edit slide'
                        : 'Edit Graphic'
                      : 'Edit Video'}
              </Button>
            )}
          </div>
          <div className="contents sm:flex sm:flex-wrap sm:justify-end sm:gap-2">
            {showDeleteAction && (
              <Button
                variant="destructive"
                className="w-full sm:w-auto"
                onClick={() => setConfirmDelete(true)}
                disabled={
                  isDeleting || isDeletingVersion || loadingGraphicVersion
                }
              >
                <Trash2 data-icon="inline-start" />
                {isDeleting || isDeletingVersion ? 'Deleting...' : 'Delete'}
              </Button>
            )}
            <Button
              variant="outline"
              className="w-full sm:w-auto"
              disabled={!url || loadingGraphicVersion}
              onClick={() => {
                if (url) window.open(url, '_blank', 'noopener,noreferrer');
              }}
            >
              <Download data-icon="inline-start" />
              Download{isCarousel ? ' slide' : ''}
            </Button>
          </div>
        </div>
      </DialogContent>
      <ConfirmDeleteDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={
          deletingVersion && !deletingLastVersion
            ? 'Delete this version?'
            : 'Delete this media?'
        }
        description={
          deletingVersion && !deletingLastVersion
            ? 'This removes this version. The original and other versions stay. This cannot be undone.'
            : isUploadedImage && (versionsQuery.data?.versions.length ?? 0) > 1
              ? 'This removes it and its edited versions from your library. This cannot be undone.'
              : 'This removes it from your library. This cannot be undone.'
        }
        isPending={isDeleting || isDeletingVersion}
        onConfirm={() => {
          if (deletingVersion && historyItemId)
            deleteContentVersion({
              itemId: historyItemId,
              graphicId: selectedId,
            });
          else onDelete?.(item);
        }}
      />
    </Dialog>
  );
}
